<template>
  <div class="ui-choice">
    <div class="ui-choice__prompt">{{ block.props.prompt }}</div>
    <div class="ui-choice__options">
      <button
        v-for="option in block.props.options"
        :key="option.key"
        type="button"
        class="ui-choice__option"
        :class="{
          'ui-choice__option--chosen': chosenKey === option.key,
          'ui-choice__option--inert': !interactive,
          'ui-choice__option--locked': option.disabled,
        }"
        :disabled="!interactive || option.disabled"
        :title="option.disabled ? option.disabled_reason : null"
        @click="!option.disabled && $emit('action', { key: option.key, label: `I chose: ${option.label}` })"
      >
        <!-- Leading icon tile (DS IntentCard): indigo wash, engine-supplied
             icon or a keyword fallback; a lock when the option is gated. -->
        <span class="ui-choice__tile" aria-hidden="true">
          <v-icon size="18" class="ui-choice__tile-icon">
            {{ option.disabled ? 'mdi-lock-outline' : optionIcon(option) }}
          </v-icon>
        </span>
        <span class="ui-choice__text">
          <span class="ui-choice__label">
            {{ option.label }}
            <span v-if="option.badge" class="ui-choice__badge">{{ option.badge }}</span>
          </span>
          <span v-if="option.disabled && option.disabled_reason" class="ui-choice__locked-reason">{{ option.disabled_reason }}</span>
          <span v-else-if="option.description" class="ui-choice__desc">{{ option.description }}</span>
        </span>
        <span v-if="option.hint && !option.disabled" class="ui-choice__hint">{{ option.hint }}</span>
        <v-icon
          v-if="!option.disabled"
          size="18"
          class="ui-choice__arrow"
        >mdi-arrow-right</v-icon>
      </button>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';

/**
 * `choice` ui_block — the phase-1 workflow component. Renders the
 * step's prompt + one button per option; clicking emits the full
 * option object and the parent routes it into a workflow action
 * message (buildWorkflowActionMessage).
 *
 * `interactive=false` (superseded step: a newer assistant message
 * exists, or the instance moved on) renders the same card inert so
 * the conversation still reads coherently on scroll-back/replay.
 * `chosenKey` highlights the recorded answer on inert cards.
 */
export default defineComponent({
  name: 'UiBlockChoice',
  props: {
    block: { type: Object, required: true },
    interactive: { type: Boolean, default: false },
    chosenKey: { type: String, default: null },
  },
  emits: ['action'],
  setup() {
    /**
     * Leading-tile icon. Prefers an engine-supplied `option.icon`
     * (spec passthrough), else keys off the option's wording — the
     * canonical guided entries (search / create / upload) match the
     * prototype IntentCard's search / add / upload_file trio.
     */
    function optionIcon(option) {
      if (option.icon) {
        return option.icon.startsWith('mdi-') ? option.icon : `mdi-${option.icon}`;
      }
      const t = `${option.key} ${option.label}`.toLowerCase();
      if (/find|search|exist|browse|look/.test(t)) return 'mdi-magnify';
      if (/upload|attach|\bjd\b|file/.test(t)) return 'mdi-upload';
      if (/create|new|draft|generate|build|start/.test(t)) return 'mdi-plus';
      if (/compare|level/.test(t)) return 'mdi-scale-balance';
      if (/edit|update|change|refine/.test(t)) return 'mdi-pencil-outline';
      if (/yes|confirm|approve|apply|accept|finish|done/.test(t)) return 'mdi-check';
      if (/no\b|skip|later|cancel|exit|back/.test(t)) return 'mdi-arrow-u-left-top';
      return 'mdi-creation';
    }
    return { optionIcon };
  },
});
</script>

<style scoped>
/* Prototype IntentCard treatment: no tinted card chrome — the option
   rows sit directly in the thread as bordered white tiles. */
.ui-choice {
  margin-top: 10px;
}
.ui-choice__prompt {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  color: rgb(55 65 81);
  margin-bottom: 10px;
}
.ui-choice__options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ui-choice__option {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 14px;
  border-radius: 12px;
  border: 1px solid rgb(229 231 235);
  background: white;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.ui-choice__option:hover:not(:disabled) {
  border-color: rgb(165 180 252);
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}
.ui-choice__option--inert {
  cursor: default;
  opacity: 0.6;
}
.ui-choice__option--chosen {
  border-color: rgb(79 70 229);
  background: rgb(238 242 255);
  opacity: 1;
}
.ui-choice__option--locked {
  cursor: not-allowed;
  opacity: 0.4;
  background: rgb(249 250 251);
}
/* Leading icon tile: 40px indigo wash, deepens on row hover. */
.ui-choice__tile {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: rgb(238 242 255);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;
}
.ui-choice__option:hover:not(:disabled) .ui-choice__tile {
  background: rgb(224 231 255);
}
.ui-choice__option--locked .ui-choice__tile {
  background: rgb(243 244 246);
}
.ui-choice__tile-icon {
  color: rgb(79 70 229) !important;
}
.ui-choice__option--locked .ui-choice__tile-icon {
  color: rgb(156 163 175) !important;
}
.ui-choice__text {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.ui-choice__label {
  font-size: 14px;
  font-weight: 500;
  color: rgb(17 24 39);
}
/* Small pill next to the label (e.g. "Recommended"). */
.ui-choice__badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 8px;
  border-radius: 999px;
  background: rgb(79 70 229);
  color: white;
  font-size: 10px;
  font-weight: 600;
  vertical-align: 1px;
}
.ui-choice__desc {
  font-size: 12.5px;
  color: rgb(107 114 128);
  line-height: 1.35;
}
.ui-choice__locked-reason {
  font-size: 11px;
  color: rgb(156 163 175);
  font-style: italic;
  line-height: 1.4;
}
/* Right-aligned meta hint (e.g. "Lowest effort") before the arrow. */
.ui-choice__hint {
  flex: 0 0 auto;
  font-size: 11px;
  color: rgb(100 116 139);
}
/* Trailing arrow: quiet grey at rest; tints indigo and nudges right on
   row hover (DS IntentCard affordance). */
.ui-choice__arrow {
  flex: 0 0 auto;
  color: rgb(209 213 219) !important;
  transition: color 0.15s ease, transform 0.15s ease;
}
.ui-choice__option:hover:not(:disabled) .ui-choice__arrow {
  color: rgb(99 102 241) !important;
  transform: translateX(2px);
}
</style>
